// src/dashboard/pages/teacher/Objectifs/ObjectifsDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useObjectifsDashboard } from "./hooks/useObjectifsDashboard";

import DashboardObjectifsHeader from "./components/DashboardObjectifsHeader";
import ObjectifsFiltersCard from "./components/ObjectifsFiltersCard";
import ObjectifsKpiCards from "./components/ObjectifsKpiCards";
import ObjectifsDataTable from "./components/ObjectifsDataTable";
import ObjectifsMobileCards from "./components/ObjectifsMobileCards";
import ObjectifsEmptyState from "./components/ObjectifsEmptyState";
import ObjectifDrawer from "./components/ObjectifDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Objectifs adhérents
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux enseignants et administrateurs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ObjectifsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    objectifs,
    filteredObjectifs,
    groups,
    adherents,
    filteredAdherents,
    seasonOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,
    updatingProgressId,

    drawerOpen,
    drawerMode,
    currentObjectif,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    handleCreateObjectif,
    handleUpdateObjectif,
    handleDeleteObjectif,
    handleToggleDone,
    handleChangeProgress,
    refreshObjectifs,
  } = useObjectifsDashboard();

  if (![1, 3].includes(roleId)) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardObjectifsHeader />

        <ObjectifsFiltersCard
          filters={filters}
          setFilters={setFilters}
          groups={groups}
          adherents={filteredAdherents}
          seasonOptions={seasonOptions}
          loading={loading}
          onAdd={openCreateDrawer}
          onRefresh={refreshObjectifs}
        />

        <ObjectifsKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Suivi pédagogique
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Objectifs de saison des adhérents
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Filtrez par groupe, saison, adhérent, type ou statut. Mettez à
                  jour la progression directement depuis le tableau.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Objectifs affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredObjectifs.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {objectifs.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredObjectifs.length === 0 ? (
              <ObjectifsEmptyState onAdd={openCreateDrawer} />
            ) : (
              <div className="mt-5">
                <ObjectifsDataTable
                  rows={filteredObjectifs}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteObjectif}
                  onToggleDone={handleToggleDone}
                  onChangeProgress={handleChangeProgress}
                  deletingId={deletingId}
                  updatingProgressId={updatingProgressId}
                />

                <ObjectifsMobileCards
                  rows={filteredObjectifs}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteObjectif}
                  onToggleDone={handleToggleDone}
                  onChangeProgress={handleChangeProgress}
                  deletingId={deletingId}
                  updatingProgressId={updatingProgressId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <ObjectifDrawer
        isOpen={drawerOpen}
        mode={drawerMode}
        objectif={currentObjectif}
        adherents={adherents}
        filteredAdherents={filteredAdherents}
        seasonOptions={seasonOptions}
        defaultSeason={filters.season}
        onClose={closeDrawer}
        onCreate={handleCreateObjectif}
        onUpdate={handleUpdateObjectif}
        saving={saving}
      />

      <ToastContainer />
    </section>
  );
}